/**
 * SearchBar Molecule
 *
 * Search input with icon, clear button, and intelligent autocomplete navigation.
 * Combines Input atom with search functionality and autocomplete dropdown.
 *
 * ## Features
 * - Real-time autocomplete with configurable debounce
 * - Intelligent navigation: automatically goes to product detail page when results are found
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Falls back to search filtering when no autocomplete results available
 *
 * ## Navigation Behavior
 * When user presses Enter or submits:
 * 1. If autocomplete results exist → Navigate to first result's detail page
 * 2. If no autocomplete results → Trigger search filter (onSearch callback)
 *
 * When user clicks autocomplete result:
 * - Always navigates to that product's detail page (via onSelectResult callback)
 *
 * ## Usage
 * ```tsx
 * <SearchBar
 *   onSearch={(query) => navigate(`/products?search=${query}`)}  // Fallback search
 *   onSelectResult={(result) => navigate(`/products/${result.id}`)}  // Navigate to product
 *   enableAutocomplete={true}
 *   debounceMs={500}
 * />
 * ```
 */

import React, { useId, useState, useEffect, useRef } from 'react';
import { Input } from '@m360-ui';
import { searchProducts, type SearchResult } from '../../api/products';
import { useToast } from '../../contexts/ToastContext';

// Get API URL from environment - use empty string in dev to leverage Vite proxy
const API_URL = import.meta.env.VITE_API_URL || '';

export interface SearchBarProps {
  /**
   * Search value
   */
  value?: string;

  /**
   * Default value (uncontrolled)
   */
  defaultValue?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Search handler
   */
  onSearch?: (query: string) => void;

  /**
   * Change handler (controlled)
   */
  onChange?: (query: string) => void;

  /**
   * Result selection handler
   */
  onSelectResult?: (result: SearchResult) => void;

  /**
   * Debounce delay in ms
   */
  debounceMs?: number;

  /**
   * Enable autocomplete dropdown
   */
  enableAutocomplete?: boolean;

  /**
   * Max autocomplete results to show
   */
  autocompleteLimit?: number;

  /**
   * Full width
   */
  fullWidth?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value: controlledValue,
  defaultValue,
  placeholder = 'Search...',
  onSearch,
  onChange,
  onSelectResult,
  debounceMs = 300,
  enableAutocomplete = true,
  autocompleteLimit = 10,
  fullWidth = false,
  className = '',
}) => {
  const inputId = useId();
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [autocompleteResults, setAutocompleteResults] = useState<SearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const isControlled = controlledValue !== undefined;

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch autocomplete results
  const fetchAutocomplete = async (query: string) => {
    if (!enableAutocomplete || !query || query.length < 2) {
      setAutocompleteResults([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchProducts(query, autocompleteLimit);
      setAutocompleteResults(results);
      setShowDropdown(results.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Autocomplete error:', error);
      addToast('Search failed. Please try again.', 'error');
      setAutocompleteResults([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    if (!isControlled) {
      setInternalValue(newValue);
    }

    if (onChange) {
      onChange(newValue);
    }

    // Debounced autocomplete only (onSearch should only be called on explicit submit)
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      fetchAutocomplete(newValue);
    }, debounceMs);

    setDebounceTimer(timer);
  };

  const handleClear = () => {
    if (!isControlled) {
      setInternalValue('');
    }

    if (onChange) {
      onChange('');
    }

    // Don't trigger onSearch when clearing - just clear the input
    // User can submit empty search explicitly if needed

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    setAutocompleteResults([]);
    setShowDropdown(false);
  };

  const handleSelectResult = (result: SearchResult) => {
    if (!isControlled) {
      setInternalValue(result.name);
    }

    if (onChange) {
      onChange(result.name);
    }

    if (onSelectResult) {
      onSelectResult(result);
    }

    setShowDropdown(false);
    setAutocompleteResults([]);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        // Only handle if dropdown is showing
        if (!showDropdown || autocompleteResults.length === 0) return;

        event.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev < autocompleteResults.length - 1 ? prev + 1 : prev;
          // Scroll into view
          setTimeout(() => {
            const element = document.querySelector(`[data-autocomplete-index="${newIndex}"]`);
            element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 0);
          return newIndex;
        });
        break;

      case 'ArrowUp':
        // Only handle if dropdown is showing
        if (!showDropdown || autocompleteResults.length === 0) return;

        event.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : -1;
          // Scroll into view
          setTimeout(() => {
            if (newIndex >= 0) {
              const element = document.querySelector(`[data-autocomplete-index="${newIndex}"]`);
              element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;

      case 'Enter':
        event.preventDefault();

        /**
         * Enter Key Navigation Logic:
         * Priority 1: If user arrow-keyed to a specific result → navigate to that product
         * Priority 2: If autocomplete showing results → navigate to first result (most relevant)
         * Priority 3: If no autocomplete results → fall back to search filter
         */
        if (showDropdown && selectedIndex >= 0 && selectedIndex < autocompleteResults.length) {
          handleSelectResult(autocompleteResults[selectedIndex]);
        } else if (showDropdown && autocompleteResults.length > 0) {
          handleSelectResult(autocompleteResults[0]);
        } else if (onSearch && value.trim()) {
          onSearch(value);
          setShowDropdown(false);
        }
        break;

      case 'Escape':
        event.preventDefault();
        setShowDropdown(false);
        break;
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    /**
     * Form Submit Navigation Logic:
     * If autocomplete found results → go directly to most relevant product
     * Otherwise → trigger search filter to show all matching products
     */
    if (autocompleteResults.length > 0) {
      handleSelectResult(autocompleteResults[0]);
    } else if (onSearch) {
      onSearch(value);
    }

    setShowDropdown(false);
  };

  const containerClass = `relative ${fullWidth ? 'w-full' : ''} ${className}`;

  return (
    <div className={containerClass} ref={dropdownRef}>
      <form onSubmit={handleSubmit} className="relative flex items-center">
        <label htmlFor={inputId} className="sr-only">
          {placeholder}
        </label>

        {/* Search Icon */}
        <div className="absolute left-3 pointer-events-none z-10">
          <svg
            className="h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Input */}
        <Input
          id={inputId}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          fullWidth={fullWidth}
          className="pl-10 pr-10"
          autoComplete="off"
        />

        {/* Required submit affordance for keyboard users; visually hidden. */}
        <button type="submit" className="sr-only">
          Search
        </button>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute right-10 pointer-events-none">
            <svg
              className="animate-spin h-4 w-4 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}

        {/* Clear Button */}
        {value && !isLoading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 text-gray-400 hover:text-gray-600 z-10"
            aria-label="Clear search"
          >
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </form>

      {/* Autocomplete Dropdown */}
      {showDropdown && autocompleteResults.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto">
          {autocompleteResults.map((result, index) => (
            <button
              key={result.id}
              type="button"
              onClick={() => handleSelectResult(result)}
              data-autocomplete-index={index}
              className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
            >
              {/* Product Image */}
              {result.hasImage && result.imagePath ? (
                <img
                  src={`${API_URL}/api/v1/images/${result.imagePath}`}
                  alt={result.name}
                  className="w-12 h-12 object-cover rounded flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder-product.png';
                  }}
                />
              ) : (
                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-6 h-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{result.name}</div>
                <div className="text-sm text-gray-500 flex items-center space-x-2">
                  {result.brand && <span>{result.brand}</span>}
                  {result.manufacturer && result.brand && <span className="text-gray-400">•</span>}
                  {result.manufacturer && <span>{result.manufacturer}</span>}
                  {result.upc && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="font-mono text-xs">{result.upc}</span>
                    </>
                  )}
                </div>
                {result.style && <div className="text-xs text-gray-400 mt-0.5">{result.style}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
